import React, { useState } from "react";
import { useDesktopsStore } from "@/dentlab/src/store/Desktops";
import { DOCUMENT_TYPES } from "@/dentlab/src/store/Desktops/types";
import type { PrinterCapabilities } from "@/dentlab/src/store/Desktops/types";
import {
	DesktopEntityTypeWithSystems,
	PrinterConfigEntityType,
	PrinterConfigEntityTypeInsert,
} from "@/lib/supabase/supabaseTypes";
import { DesktopSystemTypeEnum } from "@/lib/supabase/supabaseEnums";
import { dateOlderThanSeconds } from "@/dentlab/src/lib/utils/utils-functions";
import {
	sendTestPrintJob,
	pollPrintResponse,
} from "@/dentlab/src/lib/utils/print-utils";
import { showNotification } from "@/dentlab/src/store/Central/selectors";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePdfTemplateConfigurationContext } from "@/dentlab/src/context/PdfTemplateConfigurationContext";
import { useExportPdf } from "@/dentlab/src/hooks/useExportPdf";
import { getTestDocumentComponent } from "@/dentlab/src/lib/utils/test-print-data";
import { useRegionKind } from "@/dentlab/src/lib/utils/region-utils";
import { Logger } from "@/lib/logger/Logger";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/dentlab/src/components/ui/select";
import { Checkbox } from "@/dentlab/src/components/ui/checkbox";
import { Button } from "@/dentlab/src/components/ui/button";
import { Input } from "@/dentlab/src/components/ui/input";
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from "@/dentlab/src/components/ui/tooltip";
import {
	ChevronDown,
	ChevronRight,
	Info,
	Loader2,
	PrinterIcon,
} from "lucide-react";

interface PrinterRoutingTableProps {
	locationId: number;
	locationName: string;
	organizationId: string;
	desktopsWithSystems: DesktopEntityTypeWithSystems[];
}

const isLabelType = (docTypeValue: string) => docTypeValue.startsWith("etik_");

const getDuplexState = (
	config: PrinterConfigEntityType | undefined,
	capabilities: PrinterCapabilities | null
): { disabled: boolean; tooltip: string | null } => {
	if (!config?.desktop_system_id) {
		return { disabled: true, tooltip: "Kein Drucker ausgewählt" };
	}
	if (capabilities !== null && !capabilities.supportsDuplex) {
		return { disabled: true, tooltip: "Drucker unterstützt kein Duplex" };
	}
	return { disabled: false, tooltip: null };
};

export const PrinterRoutingTable: React.FC<PrinterRoutingTableProps> = ({
	locationId,
	locationName,
	organizationId,
	desktopsWithSystems,
}) => {
	const {
		printerConfigs,
		upsertPrinterConfig,
		getPrinterCapabilities,
		getOrgPrintMode,
	} = useDesktopsStore((state) => ({
		printerConfigs: state.printerConfigs,
		upsertPrinterConfig: state.upsertPrinterConfig,
		getPrinterCapabilities: state.getPrinterCapabilities,
		getOrgPrintMode: state.getOrgPrintMode,
	}));

	const isDesktopMode = getOrgPrintMode(locationId) === "desktop";

	const organization = useCentralStore((state) => state.organization);
	const labRegion = useRegionKind(null);
	const { pdfTemplateConfiguration } = usePdfTemplateConfigurationContext();
	const { exportPdf } = useExportPdf();

	const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>(
		{}
	);
	const [testingRows, setTestingRows] = useState<Record<string, boolean>>({});

	if (!isDesktopMode) return null;

	const configsForLocation = printerConfigs.filter(
		(c) => c.location_id === locationId
	);

	const allPrinters = desktopsWithSystems.flatMap((desktop) => {
		const isOnline = !dateOlderThanSeconds(
			new Date(desktop.last_active_at),
			60
		);
		return desktop.desktop_systems
			.filter((s) => s.type === DesktopSystemTypeEnum.PRINTER)
			.map((printer) => ({
				id: printer.id,
				name: printer.name,
				desktopName: desktop.name,
				desktopId: desktop.id,
				online: isOnline,
			}));
	});

	const getConfigForType = (documentType: string) => {
		return configsForLocation.find((c) => c.config_key === documentType);
	};

	const getCapabilities = (
		config: PrinterConfigEntityType | undefined
	): PrinterCapabilities | null => {
		if (!config?.desktop_system_id) return null;
		return getPrinterCapabilities(config.desktop_system_id);
	};

	const handleUpsert = (
		documentType: string,
		updates: Partial<PrinterConfigEntityTypeInsert>
	) => {
		const existing = getConfigForType(documentType);
		upsertPrinterConfig({
			organization_id: organizationId,
			location_id: locationId,
			config_key: documentType,
			desktop_system_id: existing?.desktop_system_id ?? null,
			...updates,
		});
	};

	const handlePrinterChange = (
		documentType: string,
		desktopSystemId: string
	) => {
		upsertPrinterConfig({
			organization_id: organizationId,
			location_id: locationId,
			config_key: documentType,
			desktop_system_id: desktopSystemId
				? parseInt(desktopSystemId)
				: null,
		});
	};

	const handleTestPrint = async (documentType: string) => {
		const config = getConfigForType(documentType);
		if (!config?.desktop_system_id) return;

		setTestingRows((prev) => ({ ...prev, [documentType]: true }));
		try {
			let testBlob: Blob | undefined;

			if (organization && pdfTemplateConfiguration) {
				try {
					const documentComponent = getTestDocumentComponent(
						documentType,
						organization,
						pdfTemplateConfiguration,
						labRegion
					);
					const result = await exportPdf({ documentComponent });
					if (result.success && result.blob) {
						testBlob = result.blob;
					}
				} catch (e) {
					Logger.error(e);
				}
			}

			const result = await sendTestPrintJob(
				config,
				desktopsWithSystems,
				testBlob
			);
			if (result.success && result.requestId) {
				if (result.desktopOnline) {
					await pollPrintResponse(result.requestId);
				} else {
					showNotification({
						message:
							"Desktop ist offline — Testdruck wird gesendet sobald der Desktop online ist",
						type: "warning",
					});
				}
			}
		} finally {
			setTestingRows((prev) => ({ ...prev, [documentType]: false }));
		}
	};

	const handleToggleExpand = (docTypeValue: string) => {
		setExpandedRows((prev) => ({
			...prev,
			[docTypeValue]: !prev[docTypeValue],
		}));
	};

	return (
		<div>
			<h2 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-3">
				Druckereinstellungen ({locationName})
			</h2>
			<div
				className="border rounded-lg overflow-hidden"
				data-testid="printer-routing-table"
			>
				<table className="w-full">
					<thead>
						<tr className="bg-gray-50 border-b">
							<th className="w-8" />
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">
								Dokumenttyp
							</th>
							<th className="text-left text-xs font-medium text-gray-500 px-4 py-2.5">
								Drucker
							</th>
							<th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5 w-20">
								Kopien
							</th>
							<th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5 w-20">
								Duplex
							</th>
							<th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5 w-20">
								Farbe
							</th>
							<th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5 w-24">
								Auto-Druck
							</th>
							<th className="text-center text-xs font-medium text-gray-500 px-4 py-2.5 w-24">
								Test
							</th>
						</tr>
					</thead>
					<tbody>
						{DOCUMENT_TYPES.map((docType) => {
							const config = getConfigForType(docType.value);
							const isLabel = isLabelType(docType.value);
							const isExpanded =
								expandedRows[docType.value] ?? false;
							const capabilities = getCapabilities(config);
							const duplex = getDuplexState(config, capabilities);

							return (
								<React.Fragment key={docType.value}>
									<tr
										className="border-b last:border-b-0 hover:bg-gray-50"
										data-testid={`printer-routing-row-${docType.value}`}
									>
										<td className="pl-2 py-2">
											<Button
												variant="ghost"
												size="sm"
												className="h-6 w-6 p-0"
												onClick={() =>
													handleToggleExpand(
														docType.value
													)
												}
												aria-label={`Erweiterte Einstellungen ${docType.label}`}
												data-testid={`printer-routing-row-expand-${docType.value}`}
											>
												{isExpanded ? (
													<ChevronDown className="h-4 w-4" />
												) : (
													<ChevronRight className="h-4 w-4" />
												)}
											</Button>
										</td>
										<td className="px-4 py-2 text-sm">
											{docType.label}
										</td>
										<td className="px-4 py-2">
											<Select
												value={
													config?.desktop_system_id?.toString() ??
													"none"
												}
												onValueChange={(v) =>
													handlePrinterChange(
														docType.value,
														v === "none" ? "" : v
													)
												}
											>
												<SelectTrigger
													className="h-8 text-sm w-64"
													data-testid={`printer-select-${docType.value}`}
												>
													<SelectValue placeholder="Nicht konfiguriert" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="none">
														Nicht konfiguriert
													</SelectItem>
													{allPrinters.map(
														(printer) => (
															<SelectItem
																key={printer.id}
																value={printer.id.toString()}
															>
																<span className="flex items-center gap-2">
																	<span
																		className={`w-1.5 h-1.5 rounded-full ${
																			printer.online
																				? "bg-green-500"
																				: "bg-gray-300"
																		}`}
																	/>
																	{
																		printer.name
																	}{" "}
																	(
																	{
																		printer.desktopName
																	}
																	)
																</span>
															</SelectItem>
														)
													)}
												</SelectContent>
											</Select>
										</td>
										<td className="px-4 py-2 text-center">
											<Input
												type="number"
												min={1}
												max={99}
												step={1}
												className="h-8 w-16 text-center text-sm mx-auto"
												value={config?.copies ?? 1}
												onChange={(e) =>
													handleUpsert(
														docType.value,
														{
															copies: Math.max(
																1,
																Math.min(
																	99,
																	parseInt(
																		e.target
																			.value
																	) || 1
																)
															),
														}
													)
												}
												aria-label={`Kopien ${docType.label}`}
												data-testid={`copies-input-${docType.value}`}
											/>
										</td>
										<td className="px-4 py-2 text-center">
											{!isLabel &&
												(duplex.tooltip ? (
													<Tooltip>
														<TooltipTrigger asChild>
															<span className="inline-flex">
																<Checkbox
																	checked={
																		config?.duplex ??
																		false
																	}
																	disabled
																/>
															</span>
														</TooltipTrigger>
														<TooltipContent>
															{duplex.tooltip}
														</TooltipContent>
													</Tooltip>
												) : (
													<Checkbox
														checked={
															config?.duplex ??
															false
														}
														onCheckedChange={(
															checked
														) =>
															handleUpsert(
																docType.value,
																{
																	duplex: checked as boolean,
																}
															)
														}
													/>
												))}
										</td>
										<td className="px-4 py-2 text-center">
											<Checkbox
												checked={
													config?.color_mode ===
													"color"
												}
												onCheckedChange={(checked) =>
													handleUpsert(
														docType.value,
														{
															color_mode: checked
																? "color"
																: "bw",
														}
													)
												}
											/>
										</td>
										<td className="px-4 py-2 text-center">
											{!docType.autoPrint ? null : isDesktopMode ? (
												<Checkbox
													checked={
														config?.auto_print ??
														false
													}
													onCheckedChange={(
														checked
													) =>
														handleUpsert(
															docType.value,
															{
																auto_print:
																	checked as boolean,
															}
														)
													}
													aria-label={`Auto-Druck ${docType.label}`}
													data-testid={`auto-print-${docType.value}`}
												/>
											) : (
												<Tooltip>
													<TooltipTrigger asChild>
														<span className="inline-flex">
															<Checkbox
																checked={false}
																disabled
															/>
														</span>
													</TooltipTrigger>
													<TooltipContent>
														Automatischer Druck ist
														nur im Desktop-Modus
														verfügbar
													</TooltipContent>
												</Tooltip>
											)}
										</td>
										<td className="px-4 py-2 text-center">
											<Button
												variant="ghost"
												size="sm"
												className="h-7 px-2"
												disabled={
													!config?.desktop_system_id ||
													testingRows[docType.value]
												}
												onClick={() =>
													handleTestPrint(
														docType.value
													)
												}
												aria-label={`Testdruck ${docType.label}`}
												data-testid={`test-print-${docType.value}`}
											>
												{testingRows[docType.value] ? (
													<Loader2 className="h-4 w-4 animate-spin" />
												) : (
													<PrinterIcon className="h-4 w-4" />
												)}
											</Button>
										</td>
									</tr>
									{isExpanded && (
										<tr
											className="border-b last:border-b-0 bg-gray-50/50"
											data-testid={`printer-routing-row-expanded-${docType.value}`}
										>
											<td />
											<td
												colSpan={7}
												className="px-4 py-3"
											>
												<ExpandedSettings
													docType={docType.value}
													config={config}
													capabilities={capabilities}
													isLabel={isLabel}
													onUpdate={(updates) =>
														handleUpsert(
															docType.value,
															updates
														)
													}
												/>
											</td>
										</tr>
									)}
								</React.Fragment>
							);
						})}
					</tbody>
				</table>
			</div>
		</div>
	);
};

interface ExpandedSettingsProps {
	docType: string;
	config: PrinterConfigEntityType | undefined;
	capabilities: PrinterCapabilities | null;
	isLabel: boolean;
	onUpdate: (updates: Partial<PrinterConfigEntityTypeInsert>) => void;
}

const ExpandedSettings: React.FC<ExpandedSettingsProps> = ({
	docType,
	config,
	capabilities,
	isLabel,
	onUpdate,
}) => {
	const hasCaps =
		capabilities &&
		(capabilities.paperSizes.length > 0 || capabilities.trays.length > 0);
	const noPrinter = !config?.desktop_system_id;

	return (
		<div className="space-y-3 max-w-lg">
			{!noPrinter && !hasCaps && (
				<div className="flex items-center gap-1.5 text-xs text-amber-600">
					<Info className="h-3.5 w-3.5 shrink-0" />
					Druckerinfo nicht verfügbar — Standardwerte werden angezeigt
				</div>
			)}
			<div className="grid grid-cols-2 gap-x-8 gap-y-3">
				<div className="flex items-center gap-3">
					<label className="text-xs text-gray-500 w-24 shrink-0">
						Papierformat
					</label>
					<Select
						value={config?.paper_size ?? "A4"}
						onValueChange={(v) => onUpdate({ paper_size: v })}
						disabled={noPrinter}
					>
						<SelectTrigger
							className="h-8 text-sm"
							data-testid={`paper-size-select-${docType}`}
						>
							<SelectValue
								placeholder={
									noPrinter
										? "Kein Drucker"
										: hasCaps
											? "Wählen..."
											: "Keine Daten — Desktop muss Drucker erkennen"
								}
							/>
						</SelectTrigger>
						<SelectContent>
							{capabilities?.paperSizes.map((ps) => (
								<SelectItem key={ps.name} value={ps.name}>
									{ps.name}
									{ps.width > 0 &&
										ps.height > 0 &&
										` (${ps.width}×${ps.height}mm)`}
								</SelectItem>
							))}
							{(!capabilities ||
								capabilities.paperSizes.length === 0) && (
								<>
									<SelectItem value="A4">A4</SelectItem>
									<SelectItem value="A5">A5</SelectItem>
									<SelectItem value="Letter">
										Letter
									</SelectItem>
								</>
							)}
						</SelectContent>
					</Select>
				</div>

				<div className="flex items-center gap-3">
					<label className="text-xs text-gray-500 w-24 shrink-0">
						Fach
					</label>
					<Select
						value={String(config?.tray_number ?? 0)}
						onValueChange={(v) =>
							onUpdate({ tray_number: parseInt(v) })
						}
						disabled={noPrinter}
					>
						<SelectTrigger
							className="h-8 text-sm"
							data-testid={`tray-select-${docType}`}
						>
							<SelectValue
								placeholder={
									noPrinter
										? "Kein Drucker"
										: hasCaps
											? "Wählen..."
											: "Keine Daten — Desktop muss Drucker erkennen"
								}
							/>
						</SelectTrigger>
						<SelectContent>
							{capabilities && capabilities.trays.length > 0 ? (
								capabilities.trays.map((tray) => (
									<SelectItem
										key={tray.id}
										value={String(tray.id)}
									>
										{tray.name}
									</SelectItem>
								))
							) : (
								<SelectItem value="0">Automatisch</SelectItem>
							)}
						</SelectContent>
					</Select>
				</div>

				{!isLabel && (
					<>
						<div className="flex items-center gap-3">
							<label className="text-xs text-gray-500 w-24 shrink-0">
								Seiten/Blatt
							</label>
							<Select
								value={String(config?.pages_per_sheet ?? 1)}
								onValueChange={(v) =>
									onUpdate({ pages_per_sheet: parseInt(v) })
								}
							>
								<SelectTrigger
									className="h-8 text-sm"
									data-testid={`pages-per-sheet-select-${docType}`}
								>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="1">1</SelectItem>
									<SelectItem value="2">2</SelectItem>
									<SelectItem value="4">4</SelectItem>
								</SelectContent>
							</Select>
						</div>
					</>
				)}
			</div>
		</div>
	);
};
